import React, { useState, useEffect } from "react";
import { manageCategoryApi } from "../../../entities/category";
import { Category } from "../../../entities/category";
import "./CategoryManagement.scss";

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({
    nameKo: "",
    nameEn: "",
  });
  const [editCategory, setEditCategory] = useState({
    nameKo: "",
    nameEn: "",
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await manageCategoryApi.getCategoryList();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await manageCategoryApi.addCategory({
        nameKo: newCategory.nameKo,
        nameEn: newCategory.nameEn,
        numOfArticles: 0,
      });

      if (response) {
        setCategories((prev) => [...prev, response]);
        setNewCategory({ nameKo: "", nameEn: "" });
        setShowAddModal(false);
      }
    } catch (error) {
      console.error("Failed to add category:", error);
    }
  };

  const handleEditCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      const response = await manageCategoryApi.updateCategory(
        editingCategory.id!,
        {
          ...editingCategory,
          nameKo: editCategory.nameKo,
          nameEn: editCategory.nameEn,
        },
      );

      if (response) {
        setCategories((prev) =>
          prev.map((cat) => (cat.id === editingCategory.id ? response : cat)),
        );
        setShowEditModal(false);
        setEditingCategory(null);
        setEditCategory({ nameKo: "", nameEn: "" });
      }
    } catch (error) {
      console.error("Failed to update category:", error);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await manageCategoryApi.deleteCategory(categoryToDelete);
      setCategories((prev) =>
        prev.filter((cat) => cat.id !== categoryToDelete),
      );
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error("Failed to delete category:", error);
    }
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setEditCategory({
      nameKo: category.nameKo,
      nameEn: category.nameEn || "",
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.nameKo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.nameEn &&
        category.nameEn.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  if (loading) {
    return (
      <div className="category-management-loading">
        <div className="loading-spinner"></div>
        <p>카테고리를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="category-management">
      <div className="category-management__header">
        <div className="header-content">
          <h1>카테고리 관리</h1>
          <p>카테고리를 추가, 수정, 삭제할 수 있습니다.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="add-category-btn"
        >
          <span className="btn-icon">➕</span>새 카테고리 추가
        </button>
      </div>

      {/* 검색 */}
      <div className="search-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="카테고리명으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* 카테고리 목록 */}
      <div className="categories-grid">
        {filteredCategories.map((category) => (
          <div key={category.id} className="category-card">
            <div className="category-content">
              <h3 className="category-name-ko">{category.nameKo}</h3>
              <p className="category-name-en">{category.nameEn}</p>
              <div className="category-stats">
                <span className="article-count">
                  {category.numOfArticles || 0}개 게시글
                </span>
              </div>
            </div>
            <div className="category-actions">
              <button
                onClick={() => openEditModal(category)}
                className="action-btn edit-btn"
              >
                수정
              </button>
              <button
                onClick={() => openDeleteModal(category.id!)}
                className="action-btn delete-btn"
              >
                삭제
              </button>
            </div>
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="empty-state">
            <p>검색 결과가 없습니다.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="add-first-category-btn"
            >
              첫 카테고리 추가하기
            </button>
          </div>
        )}
      </div>

      {/* 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>새 카테고리 추가</h3>
            <form onSubmit={handleAddCategory} className="category-form">
              <div className="form-group">
                <label htmlFor="nameKo">한국어 이름 *</label>
                <input
                  type="text"
                  id="nameKo"
                  value={newCategory.nameKo}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      nameKo: e.target.value,
                    }))
                  }
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="nameEn">영어 이름</label>
                <input
                  type="text"
                  id="nameEn"
                  value={newCategory.nameEn}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      nameEn: e.target.value,
                    }))
                  }
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewCategory({ nameKo: "", nameEn: "" });
                  }}
                  className="cancel-btn"
                >
                  취소
                </button>
                <button type="submit" className="submit-btn">
                  추가
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {showEditModal && editingCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>카테고리 수정</h3>
            <form onSubmit={handleEditCategory} className="category-form">
              <div className="form-group">
                <label htmlFor="editNameKo">한국어 이름 *</label>
                <input
                  type="text"
                  id="editNameKo"
                  value={editCategory.nameKo}
                  onChange={(e) =>
                    setEditCategory((prev) => ({
                      ...prev,
                      nameKo: e.target.value,
                    }))
                  }
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="editNameEn">영어 이름</label>
                <input
                  type="text"
                  id="editNameEn"
                  value={editCategory.nameEn}
                  onChange={(e) =>
                    setEditCategory((prev) => ({
                      ...prev,
                      nameEn: e.target.value,
                    }))
                  }
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCategory(null);
                    setEditCategory({ nameKo: "", nameEn: "" });
                  }}
                  className="cancel-btn"
                >
                  취소
                </button>
                <button type="submit" className="submit-btn">
                  수정
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>카테고리 삭제</h3>
            <p>정말로 이 카테고리를 삭제하시겠습니까?</p>
            <p className="warning-text">
              ⚠️ 이 카테고리에 속한 게시글들도 함께 삭제될 수 있습니다.
            </p>
            <div className="form-actions">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setCategoryToDelete(null);
                }}
                className="cancel-btn"
              >
                취소
              </button>
              <button onClick={handleDeleteCategory} className="delete-btn">
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
